import { type MainResultSet } from "#/services/rest/gwt/exam-results/views/type-1/multi-user.types";
import { useBreakLG } from "@monta/design-system";
import {
  FaceLaugh,
  FacePokerface,
  FaceSad,
  GrowDown,
  GrowNeutral,
  GrowUp,
  Info
} from "@monta/icons";
import clsx from "clsx";
import dayjs from "dayjs";
import { truncate } from "lodash";
import { useMemo, useState } from "react";
import styles from "./styles.module.scss";

type Props = {
  takingDate?: string;
  name: string;
  rank: number;
  level: number;
  totalStudentNO: number;
  tierNO: number;
  presentStudentNo?: number;
  growthStatus: MainResultSet["growthStatus"];
};

export const growthStatusGenerator = (
  status: MainResultSet["growthStatus"],
  iconSize: number
) => {
  switch (status) {
    case 1:
      return {
        label: "پیشرفت کردی",
        icon: <GrowUp size={iconSize} />
      };
    case -1:
      return {
        label: "افت کردی",
        icon: <GrowDown size={iconSize} />
      };
    case 0:
      return {
        label: "تغییر نکردی",
        icon: <GrowNeutral size={iconSize} />
      };
    case null:
      return null;
    default:
      return null;
  }
};

const GeneralInfoHeader = (props: Props) => {
  const {
    level,
    name,
    rank,
    totalStudentNO,
    takingDate,
    tierNO,
    presentStudentNo,
    growthStatus
  } = props;

  const breakLg = useBreakLG();
  const [showTooltip, setShowTooltip] = useState(false);
  const growth = growthStatusGenerator(growthStatus, breakLg ? 20 : 14);

  const generateTithe = useMemo(() => {
    if ([1, 2, 3].includes(tierNO))
      return {
        icon: <FaceLaugh size={breakLg ? 40 : 20} />,
        text: "خــــــــــــــــــــوب",
        className: styles["tithe__text--green"]
      };
    if ([4, 5, 6].includes(tierNO))
      return {
        icon: <FacePokerface size={breakLg ? 40 : 20} />,
        text: "متـــــــــــــــــوسط",
        className: styles["tithe__text--blue"]
      };
    if ([7, 8, 9, 10].includes(tierNO))
      return {
        icon: <FaceSad size={breakLg ? 40 : 20} />,
        text: "ضعـیـــــــــــــــــف",
        className: styles["tithe__text--red"]
      };
  }, [breakLg, tierNO]);

  return (
    <>
      <section className={styles["header"]}>
        <div className={styles["header__title"]}>
          <div className={styles["header__title-name"]}>
            {`گزارش کارنامه آزمون ${name}`.length >= 45 && !breakLg ? (
              <>
                <span>
                  {truncate(`گزارش کارنامه آزمون ${name}`, { length: 45 })}
                </span>
                <Info
                  onClick={() => setShowTooltip((prev) => !prev)}
                  size={20}
                />
                {showTooltip && (
                  <span className={styles["header__title-tooltip"]}>
                    گزارش کارنامه آزمون {name}
                  </span>
                )}
              </>
            ) : (
              `گزارش کارنامه آزمون ${name}`
            )}
          </div>
          <div className={styles["header__title-info"]}>
            <span>
              {presentStudentNo} نفر از {totalStudentNO} نفر در آزمون حاضر بودند
            </span>
            <span>
              {dayjs(takingDate).calendar("jalali").format("YYYY/MM/DD")}
            </span>
          </div>
        </div>
        <div className={styles["header__bottom-section"]}>
          <div className={styles["header__bottom-section__items"]}>
            <div className={styles["header__bottom-section__item"]}>
              <div className={styles["header__bottom-section__item__title"]}>
                رتبه
                {breakLg ? "" : ":"}
              </div>
              <div className={styles["header__bottom-section__item__subtitle"]}>
                {rank + " "}
                از
                {" " + totalStudentNO}
              </div>
            </div>
            <div className={styles["header__bottom-section__item"]}>
              <div className={styles["header__bottom-section__item__title"]}>
                تراز{breakLg ? "" : ":"}
              </div>
              <div className={styles["header__bottom-section__item__subtitle"]}>
                {level}
              </div>
            </div>
            {!breakLg && (
              <div
                className={clsx(
                  styles["header__bottom-section__item"],
                  styles["header__bottom-section__presents"]
                )}
              >
                <div className={styles["header__bottom-section__item__title"]}>
                  حاضرین{breakLg ? "" : ":"}
                </div>
                <div
                  className={styles["header__bottom-section__item__subtitle"]}
                >
                  {presentStudentNo} نفر از {totalStudentNO} نفر
                </div>
              </div>
            )}
            {growth !== null ? (
              <div className={styles["header__bottom-section__item"]}>
                <div className={styles["header__bottom-section__item__title"]}>
                  وضعیت رشد کلی{breakLg ? "" : ":"}
                </div>
                <div
                  className={styles["header__bottom-section__item__subtitle"]}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px"
                  }}
                >
                  <span>{growth.label}</span>
                  {growth.icon}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className={clsx(styles["tithe"], generateTithe?.className)}>
            <div className={styles["tithe__text"]}>
              {generateTithe?.icon}
              <span className={styles["tithe__text--title"]}>
                جزو دهک {tierNO} هستید
              </span>
              <span className={styles["tithe__divider"]}>|</span>
              <span>{generateTithe?.text}</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GeneralInfoHeader;
